import React, { useEffect, useMemo, useState } from "react";
import { Title } from "../../../../../../stories/Title/Title";
import { Button } from "../../../../../../stories/Button/Button";
import { Label } from "../../../../../../stories/Label/Label";
import { LabelProps, TableHeaderProps } from "../../../../../../utils/types";
import Input from "../../../../../../stories/Input/Input";
import TextArea from "../../../../../../stories/TextArea/TextArea";
import Assign from "../../../../../../images/Assign.svg";
import useAssetTableJson from "../../../../../../json/useAssetTableJson";
import {
  GridAlignment,
  GridColDef,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { StyledDatagrid } from "../../../../../../styles/DatagridStyles";
import { TABLE_ROW_HEIGHT } from "../../../../../../styles/Variables";
import { useNavigate } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import {
  AtsRfqTrnEdge,
  AtsRfqTrnOrderField,
  AtsRfqTrnSupply,
  GetRfqTrnsResult,
  GetRfqTrnSupplyByKeyResult,
  GetRfqTrnSupplyByKeyVar,
  GetRfqTrnSupplyResult,
  GetRfqTrnSupplyVars,
} from "../queries/types";

import {
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
} from "../../../../../../utils/constants";
import { OrderDirection } from "../../../../../../utils/commonQueryTypes";
import {
  getModifiedScrollHeight,
  toInputStandardDate,
} from "../../../../../../utils/UtilFunctions";
import { quotationForm } from "../../../../../../utils/formTypes";
import { GetRfqTrnSupply, GetRfqTrnSupplyByKey } from "../queries/queries";
import useUrlParams from "../../../../../../customHooks/useUrlParams";
import LoadingModal from "../../../../../pages/LoadingModal";
import useLabels from "../../../../../../customHooks/useLabels";
import useFormJson from "../../../../../../json/useFormJson";



export const rfqInputJson = [
  [
    {
      labelName: "Last Date for Submission",
      inputName: "lastDateForSubmission",
      dataType: "date",
    },
    {
      labelName: "GST No.",
      inputName: "gstNo",
      dataType: "text",
    },
    {
      labelName: "Company Name",
      inputName: "companyName",
      dataType: "text",
    },
  ],
  [
    {
      labelName: "Requested Quotation No.",
      inputName: "rfqNo",
      dataType: "text",
    },
    {
      labelName: "PAN No.",
      inputName: "panNo",
      dataType: "text",
    },
    {
      labelName: "City",
      inputName: "city",
      dataType: "text",
    },
  ],
  [
    {
      labelName: "Narration",
      inputName: "narration",
      dataType: "textarea",
    },
  ],
];

const Preview = () => {
  const { Purchase } = useAssetTableJson();
  const navigate = useNavigate();
  const {Quotation}=useFormJson();
  const {companyLabel,itemLabel}=useLabels();

  const { uuid } = useUrlParams();
  const [GetRfqTrans, { data, loading, fetchMore }] = useLazyQuery<
    GetRfqTrnSupplyByKeyResult,
    GetRfqTrnSupplyByKeyVar
  >(GetRfqTrnSupplyByKey);

  const [items, setItems] = useState<AtsRfqTrnSupply[]>([]);

  const [searchData, setSearchData] = useState("");
  const initialValue: quotationForm = {
    lastDateForSubmission: "",
    gstNo: "",
    companyName: "",
    rfqNo: "",
    panNo: "",
    city: "",
    narration: "",
    generalPaymentTerms: "",
    generalTerms: "",
  };

  const [formData, setFormData] = useState(initialValue);

  const dynamicHeaders: TableHeaderProps[] =
    Purchase.Quotation.Login.Preview.map((header) => ({
      headerName: header.headerName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
    }));
  const columns: GridColDef[] = [...dynamicHeaders];

  useEffect(() => {
    if (uuid) {
      GetRfqTrans({
        variables: {
          key: uuid,
        },
      }).then(({ data }) => {
        if (
          data &&
          data.GetRfqTrnSupplyByKey &&
          data.GetRfqTrnSupplyByKey.length
        ) {
          const firstElement = data.GetRfqTrnSupplyByKey[0];
          const rfqDetails = firstElement.rfq;
          const supplier_details = firstElement.supplierDetails;

          setFormData({
            city: supplier_details.supplier_details.city,
            companyName: supplier_details.desc,
            gstNo: supplier_details.supplier_details.gstNo,
            lastDateForSubmission: toInputStandardDate(
              rfqDetails.lastDateRfqSubmission
            ),
            narration: rfqDetails.remarks,
            panNo: supplier_details.supplier_details.panNo,
            rfqNo: rfqDetails.rfqNo,
            generalPaymentTerms: firstElement.rfq.generalPaymentTerms,
            generalTerms: firstElement.rfq.generalTerms,
          });

          setItems(data.GetRfqTrnSupplyByKey);
        }
      });
    }
  }, [GetRfqTrans, searchData, data]);

  const rowsData = useMemo(() => {
    return items.map((itm, index) => ({
      id: index + 1,
      item_desc: itm.rfqTrn.udfItemDesc,
      required_qty: itm.rfqTrn.mrf_trn_details.reqQty,
      terms: itm.rfqTrn.itemTerms,
      payment_terms: itm.rfqTrn.itemPaymentTerms,
      remarks: itm.rfqTrn.remarks,
    }));
  }, [items]);

  return (
    <>
      <div className="quotation-preview">
        <div className="quotation-preview__block">
          <Title>Quotation Preview</Title>
          <div className="quotation-preview__block--filters">
            {rfqInputJson.map((array, colIndex) => (
              <div key={colIndex}>
                {array.map((label: LabelProps) => (
                  <div className="quotation-preview__block--filters--grid">
                    <Label>{label.labelName}</Label>
                    {label.dataType === "textarea" ? (
                      <TextArea
                        rows={3}
                        value={formData[label.inputName]}
                        disabled
                      />
                    ) : (
                      <Input
                        disabled
                        type={label.dataType}
                        value={formData[label.inputName]}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="quotation-preview__block--input row g-0">
            <div className="col-2">
              <Input
                id="data_search"
                placeholder="Search..."
                onChange={(e) => {
                  setSearchData(e.target.value);
                }}
              />
            </div>
            <div className="col flex-end">
              <div className="overall-total-count">
                Total {itemLabel} Requested <b>0</b>
              </div>
            </div>
          </div>
          <div className="quotation-preview__block--tableblock">
            <StyledDatagrid
              columns={columns}
              rows={rowsData}
              rowHeight={TABLE_ROW_HEIGHT}
              disableRowSelectionOnClick
              hideFooter
            />
          </div>
          <div className="quotation-preview__block--datablock row g-0">
            <div className="col">
              <div className="label-grid">
                <Label>Our General Terms</Label>
                <TextArea rows={3} disabled value={formData.generalTerms} />
              </div>
            </div>
            <div className="col">
              <div className="label-grid">
                <Label>Our General Payment Terms</Label>
                <TextArea
                  rows={3}
                  disabled
                  value={formData.generalPaymentTerms}
                />
              </div>
            </div>
          </div>
          <Button
            mode="proceed"
            onClick={() => navigate(`/vendor/${uuid}/quotationform/details`)}
          />
        </div>
      </div>
      <LoadingModal flag={loading} />
    </>
  );
};

export default Preview;
